import fs from 'fs-extra';
import path from 'path';
import { parseString, Builder } from 'xml2js';
import { ImageProcessor } from '../utils/image';
import { SplashConfig } from '../types/config';
import { AndroidTemplates } from '../templates/android-templates';
import { FlavorHelper } from '../core/flavor-helper';

export class AndroidPlatform {
  constructor(private flavorHelper: FlavorHelper) {}

  async generateSplash(config: SplashConfig): Promise<void> {
    console.log('[Android] Generating splash screen...');

    // Generate images
    await this.generateImages(config);

    // Generate background files
    await this.generateBackgrounds(config);

    // Update XML files
    await this.updateLaunchBackground(config);
    await this.updateStyles(config);

    // Update AndroidManifest.xml for orientation
    if (config.androidScreenOrientation) {
      await this.updateManifestOrientation(config.androidScreenOrientation);
    }

    console.log('[Android] Splash screen generation complete!');
  }

  private async generateImages(config: SplashConfig): Promise<void> {
    const imagePath = config.imageAndroid || config.image;
    const darkImagePath = config.darkImageAndroid || config.darkImage;
    const brandingPath = config.brandingAndroid || config.branding;
    const brandingDarkPath = config.brandingDarkAndroid || config.brandingDark;

    // Main splash images
    if (imagePath) {
      await this.generateImageSet(imagePath, 'splash.png', false);
    }

    if (darkImagePath) {
      await this.generateImageSet(darkImagePath, 'splash.png', true);
    }

    // Branding images
    if (brandingPath) {
      await this.generateImageSet(brandingPath, 'branding.png', false);
    }

    if (brandingDarkPath) {
      await this.generateImageSet(brandingDarkPath, 'branding.png', true);
    }

    // Android 12 images
    console.log('tjhis is for android 12 splash', config);
    if (config.android12?.image) {
      await this.generateImageSet(config.android12.image, 'android12splash.png', false, true);
    }

    if (config.android12?.darkImage) {
      await this.generateImageSet(config.android12.darkImage, 'android12splash.png', true, true);
    }

    if (config.android12?.branding) {
      await this.generateImageSet(config.android12.branding, 'android12branding.png', false, true);
    }

    if (config.android12?.brandingDark) {
      await this.generateImageSet(
        config.android12.brandingDark,
        'android12branding.png',
        true,
        true
      );
    }
  }

  private async generateImageSet(
    imagePath: string,
    fileName: string,
    dark = false,
    android12 = false
  ): Promise<void> {
    const templates = ImageProcessor.getAndroidTemplates(dark, android12);
    await ImageProcessor.processImage(
      imagePath,
      templates.map(t => ({ ...t, fileName })),
      this.flavorHelper.androidMainResFolder
    );
  }

  private async generateBackgrounds(config: SplashConfig): Promise<void> {
    const color = config.colorAndroid || config.color;
    const darkColor = config.darkColorAndroid || config.darkColor;
    const backgroundImage = config.backgroundImageAndroid || config.backgroundImage;
    const darkBackgroundImage = config.darkBackgroundImageAndroid || config.darkBackgroundImage;

    // Light background
    await this.createBackground(
      color,
      backgroundImage,
      path.join(this.flavorHelper.androidDrawableFolder, 'background.png')
    );

    // Dark background
    if (darkColor || darkBackgroundImage) {
      await this.createBackground(
        darkColor,
        darkBackgroundImage,
        path.join(this.flavorHelper.androidDrawableNightFolder, 'background.png')
      );
    }

    // Also create v21 versions
    await this.createBackground(
      color,
      backgroundImage,
      path.join(this.flavorHelper.androidMainResFolder, 'drawable-v21/background.png')
    );

    if (darkColor || darkBackgroundImage) {
      await this.createBackground(
        darkColor,
        darkBackgroundImage,
        path.join(this.flavorHelper.androidMainResFolder, 'drawable-night-v21/background.png')
      );
    }
  }

  private async createBackground(
    color?: string,
    backgroundImage?: string,
    outputPath?: string
  ): Promise<void> {
    if (!outputPath) return;

    if (backgroundImage) {
      await ImageProcessor.copyAndConvertImage(backgroundImage, outputPath, 'png');
    } else if (color) {
      await ImageProcessor.createSolidColorImage(color, 1, 1, outputPath);
    }
  }

  private async updateLaunchBackground(config: SplashConfig): Promise<void> {
    const gravity = config.androidGravity || 'center';
    const showImage = !!(config.imageAndroid || config.image);
    const showBranding = !!(config.brandingAndroid || config.branding);
    const brandingMode = config.brandingMode || 'bottom';
    const brandingPadding =
      config.brandingBottomPaddingAndroid || config.brandingBottomPadding || 0;

    // Update main launch background
    await this.writeLaunchBackgroundXml(
      this.flavorHelper.androidLaunchBackgroundFile,
      gravity,
      showImage,
      showBranding,
      brandingMode,
      brandingPadding
    );

    // Update dark launch background if needed
    const hasDarkBackground = !!(
      config.darkColorAndroid ||
      config.darkColor ||
      config.darkBackgroundImageAndroid ||
      config.darkBackgroundImage
    );

    if (hasDarkBackground) {
      await this.writeLaunchBackgroundXml(
        this.flavorHelper.androidLaunchDarkBackgroundFile,
        gravity,
        showImage,
        showBranding,
        brandingMode,
        brandingPadding
      );
    }
  }

  private async writeLaunchBackgroundXml(
    filePath: string,
    gravity: string,
    showImage: boolean,
    showBranding: boolean,
    brandingMode: string,
    brandingPadding: number
  ): Promise<void> {
    console.log(`[Android] Updating ${filePath}`);

    await fs.ensureDir(path.dirname(filePath));

    let xml = AndroidTemplates.launchBackgroundXml;

    if (showImage) {
      xml = xml.replace(
        '</layer-list>',
        `    <item>
        <bitmap android:gravity="${gravity}" android:src="@drawable/splash" />
    </item>
</layer-list>`
      );
    }

    if (showBranding && brandingMode !== gravity) {
      let brandingGravity = brandingMode;
      if (brandingMode === 'bottomRight') {
        brandingGravity = 'bottom|right';
      } else if (brandingMode === 'bottomLeft') {
        brandingGravity = 'bottom|left';
      }

      const brandingItem = AndroidTemplates.brandingItemXml
        .replace('{bottom_padding}', brandingPadding.toString())
        .replace('center', brandingGravity);

      xml = xml.replace('</layer-list>', `    ${brandingItem}</layer-list>`);
    }

    await fs.writeFile(filePath, xml);
  }

  private async updateStyles(config: SplashConfig): Promise<void> {
    console.log('[Android] Updating styles...');

    const fullscreen = config.fullscreen || false;

    // Update main styles
    await this.updateStylesFile(
      this.flavorHelper.androidStylesFile,
      AndroidTemplates.stylesXml,
      fullscreen
    );

    // Update night styles
    await this.updateStylesFile(
      this.flavorHelper.androidStylesNightFile,
      AndroidTemplates.stylesNightXml,
      fullscreen
    );

    // Update v31 styles (Android 12+)
    await this.updateStylesFile(
      this.flavorHelper.androidStylesV31File,
      AndroidTemplates.stylesV31Xml,
      fullscreen,
      config.android12
    );

    // Update v31 night styles
    await this.updateStylesFile(
      this.flavorHelper.androidStylesV31NightFile,
      AndroidTemplates.stylesV31NightXml,
      fullscreen,
      config.android12
    );
  }

  private async updateStylesFile(
    filePath: string,
    template: string,
    fullscreen: boolean,
    android12Config?: any
  ): Promise<void> {
    console.log(`[Android] Updating ${filePath}`);

    await fs.ensureDir(path.dirname(filePath));

    if (!(await fs.pathExists(filePath))) {
      await fs.writeFile(filePath, template);
    }

    const content = await fs.readFile(filePath, 'utf-8');

    parseString(content, async (err, result) => {
      if (err) {
        console.error(`Error parsing ${filePath}:`, err);
        return;
      }

      // Find LaunchTheme style
      const resources = result.resources;
      if (!resources || !resources.style) return;

      const launchTheme = resources.style.find((style: any) => style.$.name === 'LaunchTheme');

      if (!launchTheme) return;

      // Update items
      if (!launchTheme.item) launchTheme.item = [];

      this.updateStyleItem(launchTheme, 'android:forceDarkAllowed', 'false');
      this.updateStyleItem(launchTheme, 'android:windowFullscreen', fullscreen.toString());
      this.updateStyleItem(
        launchTheme,
        'android:windowDrawsSystemBarBackgrounds',
        (!fullscreen).toString()
      );
      this.updateStyleItem(launchTheme, 'android:windowLayoutInDisplayCutoutMode', 'shortEdges');

      // Android 12 specific styles
      if (android12Config && filePath.includes('v31')) {
        if (android12Config.color) {
          this.updateStyleItem(
            launchTheme,
            'android:windowSplashScreenBackground',
            `#${android12Config.color}`
          );
        }
        if (android12Config.image) {
          this.updateStyleItem(
            launchTheme,
            'android:windowSplashScreenAnimatedIcon',
            '@drawable/android12splash'
          );
        }
        if (android12Config.iconBackgroundColor) {
          this.updateStyleItem(
            launchTheme,
            'android:windowSplashScreenIconBackgroundColor',
            `#${android12Config.iconBackgroundColor}`
          );
        }
        if (android12Config.branding) {
          this.updateStyleItem(
            launchTheme,
            'android:windowSplashScreenBrandingImage',
            '@drawable/android12branding'
          );
        }
      }

      // Convert back to XML
      const builder = new Builder({ headless: true });
      const xml = builder.buildObject(result);
      await fs.writeFile(filePath, `<?xml version="1.0" encoding="utf-8"?>\n${xml}\n`);
    });
  }

  private updateStyleItem(launchTheme: any, name: string, value: string): void {
    const existingIndex = launchTheme.item.findIndex((item: any) => item.$.name === name);

    if (existingIndex >= 0) {
      launchTheme.item[existingIndex]._ = value;
    } else {
      launchTheme.item.push({
        $: { name },
        _: value,
      });
    }
  }

  private async updateManifestOrientation(orientation: string): Promise<void> {
    const manifestPath = this.flavorHelper.androidManifestFile;

    if (!(await fs.pathExists(manifestPath))) {
      console.warn('[Android] AndroidManifest.xml not found, skipping orientation update');
      return;
    }

    console.log('[Android] Updating AndroidManifest.xml orientation...');

    const content = await fs.readFile(manifestPath, 'utf-8');

    parseString(content, async (err, result) => {
      if (err) {
        console.error('Error parsing AndroidManifest.xml:', err);
        return;
      }

      const manifest = result.manifest;
      const application = manifest?.application?.[0];
      const activity = application?.activity?.[0];

      if (activity) {
        activity.$['android:screenOrientation'] = orientation;

        const builder = new Builder({ headless: true });
        const xml = builder.buildObject(result);
        await fs.writeFile(manifestPath, `<?xml version="1.0" encoding="utf-8"?>\n${xml}\n`);
      }
    });
  }

  async removeSplash(): Promise<void> {
    console.log('[Android] Removing splash screen...');

    // Remove image files
    const imageFolders = [
      'drawable-mdpi',
      'drawable-hdpi',
      'drawable-xhdpi',
      'drawable-xxhdpi',
      'drawable-xxxhdpi',
      'drawable-night-mdpi',
      'drawable-night-hdpi',
      'drawable-night-xhdpi',
      'drawable-night-xxhdpi',
      'drawable-night-xxxhdpi',
    ];

    for (const folder of imageFolders) {
      const folderPath = path.join(this.flavorHelper.androidMainResFolder, folder);
      if (await fs.pathExists(folderPath)) {
        const splashFile = path.join(folderPath, 'splash.png');
        const brandingFile = path.join(folderPath, 'branding.png');

        if (await fs.pathExists(splashFile)) {
          await fs.remove(splashFile);
        }
        if (await fs.pathExists(brandingFile)) {
          await fs.remove(brandingFile);
        }
      }
    }

    console.log('[Android] Splash screen removal complete!');
  }
}
